import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './spareForwardingsModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SpareForwarding {
  id: number;
  ref?: string | null;
  caseId: number;
  purchaseOrderId?: number | null;
  forwarderId?: number | null;
  portId?: number | null;
  etaToPort?: string | null;
  statusId?: number | null;
  costUsd?: string | number | null;
  priorityId?: number | null;
  warehouseLocationId?: number | null;
  warehouseArrivalDate?: string | null;
  warehouseDepartureDate?: string | null;
  warehouseInvoiceNo?: string | null;
  warehouseCostUsd?: string | number | null;
  subAgentArrivalDate?: string | null;
  subAgentDeliveryDate?: string | null;
  subAgentInvoiceNo?: string | null;
  subAgentCostUsd?: string | number | null;
  vesselAgentArrivalDate?: string | null;
  vesselAgentDeliveryDate?: string | null;
  vesselAgentInvoiceNo?: string | null;
  vesselAgentCostUsd?: string | number | null;
  packageCount?: number | null;
  totalWeight?: string | number | null;
  notes?: string | null;
  logisticsHistory?: unknown;
  createdAt: string;
  updatedAt: string;
  spareCase?: {
    id: number;
    ref?: string | null;
    caseDate?: string | null;
    eta?: string | null;
    description?: string | null;
    port?: { name: string; code?: string | null } | null;
    vessel?: { id: number; vesselName: string; vesselAbrv?: string | null } | null;
    status?: { id: number; name: string; isActive?: boolean } | null;
  } | null;
  purchaseOrder?: {
    id: number;
    ref?: string | null;
    orderDate?: string | null;
    supplierId?: number;
    exchangeRate?: string | number | null;
    currency?: { id?: number; code?: string | null; symbol?: string | null; name?: string | null } | null;
    lineItems?: { lineTotal?: string | number | null }[];
    expenses?: { value?: string | number | null; vat?: string | number | null }[];
    _count?: { deliveries: number };
  } | null;
  forwarder?: { id: number; name: string; code?: string | null; email?: string | null; isActive?: boolean } | null;
  port?: { id: number; name: string; code?: string | null } | null;
  status?: { id: number; code: string; name: string; sortOrder?: number; isActive?: boolean } | null;
  priority?: { id: number; code: string; name: string; sortOrder?: number; isActive?: boolean } | null;
  warehouseLocation?: { id: number; name: string; code?: string | null; forwarderId?: number } | null;
  _count?: { deliveries: number };
}

export const useSpareForwardings = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<SpareForwarding> | SpareForwarding[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SpareForwarding>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSpareForwarding = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SpareForwarding>(
    id != null && Number.isFinite(id) && id > 0 ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
